import { promiseTimeout } from '@vueuse/core'

// eslint-disable-next-line @typescript-eslint/ban-types
async function waitInterval(callback: Function, ms: number, timesToRetry = Infinity) {
  let retryCount = 0
  // eslint-disable-next-line @typescript-eslint/ban-types
  return new Promise((resolve: Function, reject: Function) => {
    const interval = setInterval(async () => {
      retryCount = retryCount + 1
      if (await callback(interval)) {
        resolve()
        clearInterval(interval)
      }
      if (retryCount > timesToRetry) {
        clearInterval(interval)
        reject()
      }
    }, ms)
  })
}

export interface WithRetryOptions {
  maxRetries?: number
  delays?: number[]
  onRetry?: (attempt: number, error: unknown) => void
}

/**
 * Execute an async function with retry logic and gradual backoff
 * @param fn - The async function to execute
 * @param options - Retry options
 * @returns The result of the async function
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: WithRetryOptions = {}
): Promise<T> {
  const { maxRetries = 3, delays = [1000, 2000, 3000], onRetry } = options
  let lastError: unknown

  const getDelay = (attempt: number): number => {
    if (delays.length === 0) return 1000
    return delays[attempt] ?? delays[delays.length - 1]
  }

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      onRetry?.(attempt + 1, error)
      if (attempt < maxRetries - 1) {
        await promiseTimeout(getDelay(attempt))
      }
    }
  }

  throw lastError
}

export const asyncHelper = () => {
  return {
    waitInterval,
    withRetry
  }
}
